import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Home, Plus, Trash2 } from "lucide-react"
import { Button } from "../components/ui/button.tsx"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog.tsx"
import { Input } from "../components/ui/input.tsx"
import { Label } from "../components/ui/label.tsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.tsx"
import { ProfileLayout } from "../components/ProfileLayout"

export default function AddressPage() {
  const navigate = useNavigate()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [addresses, setAddresses] = useState([])
  const [formData, setFormData] = useState({
    building: "",
    area: "",
    landmark: "",
    state: "",
    city: "",
    phone: "",
    pincode: ""
  })

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setAddresses([...addresses, { ...formData, id: Date.now() }])
    setFormData({ building: "", area: "", landmark: "", state: "", city: "", phone: "", pincode: "" })
    setIsDialogOpen(false)
  }

  const handleDelete = (id) => {
    setAddresses(addresses.filter(addr => addr.id !== id))
  }

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-5xl">

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-semibold">My Addresses</h1>
          <Button className="bg-red-700 hover:bg-red-800 gap-2" onClick={() => setIsDialogOpen(true)}>
            <Plus className="w-4 h-4" />
            Add New Address
          </Button>
        </div>

        {addresses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <Home className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">No Addresses Found</h2>
              <p className="text-gray-600 mb-6">You haven't added any addresses yet.</p>
              <Button className="bg-red-700 hover:bg-red-800" onClick={() => setIsDialogOpen(true)}>
                Add New Address
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            {addresses.map((address) => (
              <div key={address.id} className="bg-white rounded-lg shadow-sm p-6 relative">
                <button
                  onClick={() => handleDelete(address.id)}
                  className="absolute top-4 right-4 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <p className="font-medium">{address.building}</p>
                <p className="text-gray-600">{address.area}</p>
                <p className="text-gray-600">{address.landmark}</p>
                <p className="text-gray-600">{address.city}, {address.state} - {address.pincode}</p>
                <p className="text-gray-600">Phone: +91 {address.phone}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Address</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Building Name, Flat Number <span className="text-red-600">*</span></Label>
                <Input required value={formData.building} onChange={(e) => setFormData({...formData, building: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Area, Street, Sector <span className="text-red-600">*</span></Label>
                <Input required value={formData.area} onChange={(e) => setFormData({...formData, area: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Landmark <span className="text-red-600">*</span></Label>
                <Input required value={formData.landmark} onChange={(e) => setFormData({...formData, landmark: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>State <span className="text-red-600">*</span></Label>
                <Select required value={formData.state} onValueChange={(value) => setFormData({...formData, state: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="--Select--" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="telangana">Telangana</SelectItem>
                    <SelectItem value="andhra">Andhra Pradesh</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City <span className="text-red-600">*</span></Label>
                <Select required value={formData.city} onValueChange={(value) => setFormData({...formData, city: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="--Select--" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hyderabad">Hyderabad</SelectItem>
                    <SelectItem value="secunderabad">Secunderabad</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phone Number <span className="text-red-600">*</span></Label>
                <div className="flex gap-2">
                  <Input value="+91" disabled className="w-16" />
                  <Input required type="tel" maxLength={10} value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pincode <span className="text-red-600">*</span></Label>
              <Input required maxLength={6} value={formData.pincode} onChange={(e) => setFormData({...formData, pincode: e.target.value})} className="w-1/2" />
            </div>
            <div className="flex justify-end">
              <Button type="submit" className="bg-red-700 hover:bg-red-800">Add</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </ProfileLayout>
  )
}
